import { useEffect, useRef, useState } from 'react';
import { getInitialImageIndex } from '../../lib/productImages';
import { Lens, measureLens, ZOOM_SCALE } from './lens';
import { usePinchZoom } from './usePinchZoom';
import './ProductGallery.css';

const DESKTOP_HOVER_QUERY = '(min-width: 801px) and (hover: hover) and (pointer: fine)';

interface Props {
  images: string[];
  mainImage: string;
  alt: string;
}

// Owns image presentation and interaction; supplied product data stays unchanged.
export default function ProductGallery({ images, mainImage, alt }: Props) {
  // Selection, load state and the lens are remembered per URL, so they go stale on their own
  // when the image (or the product's image list) changes instead of being reset by effects.
  const [selectedUrl, setSelectedUrl] = useState<string | null>(null);
  const active =
    selectedUrl !== null && images.includes(selectedUrl)
      ? images.indexOf(selectedUrl)
      : getInitialImageIndex(images, mainImage);
  const [loadedUrl, setLoadedUrl] = useState<string | null>(null);
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const [lensState, setLensState] = useState<(Lens & { url: string }) | null>(null);
  const gallery = useRef<HTMLDivElement>(null);
  const image = useRef<HTMLImageElement>(null);
  const opener = useRef<HTMLButtonElement>(null);
  const dialog = useRef<HTMLDialogElement>(null);
  const url = images[active];
  const loaded = loadedUrl === url;
  const failed = failedUrl === url;
  const lens = lensState?.url === url ? lensState : null;
  const setLens = (next: Lens | null) => setLensState(next && { ...next, url });
  const [viewerOpen, setViewerOpen] = useState(false);
  const surface = useRef<HTMLDivElement>(null);
  const enlarged = useRef<HTMLImageElement>(null);
  const { zoom, resetZoom, toggleZoom, lastPointerType, handlers } = usePinchZoom(surface, enlarged, failed);
  // Zoom lives partly in refs (pointers, gesture, taps), so it is reset imperatively per image.
  useEffect(() => {
    resetZoom();
  }, [url, resetZoom]);
  useEffect(() => {
    if (!viewerOpen) return;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('resize', resetZoom);
    return () => {
      document.body.style.overflow = overflow;
      window.removeEventListener('resize', resetZoom);
    };
  }, [viewerOpen, resetZoom]);

  const select = (index: number) => {
    setLens(null);
    setSelectedUrl(images[index]);
  };
  const move = (step: number) => select((active + step + images.length) % images.length);
  const arrows = () =>
    images.length > 1 && (
      <>
        <button
          type="button"
          className="product-gallery__previous"
          aria-label="Previous image"
          onClick={() => move(-1)}
        >
          ‹
        </button>
        <button
          type="button"
          className="product-gallery__next"
          aria-label="Next image"
          onClick={() => move(1)}
        >
          ›
        </button>
      </>
    );

  const hover = (event: React.PointerEvent) => {
    const img = image.current;
    const layout = gallery.current?.parentElement;
    if (!loaded || failed || event.pointerType === 'touch' || !window.matchMedia(DESKTOP_HOVER_QUERY).matches)
      return;
    if (!img || !layout) return;
    setLens(measureLens({ x: event.clientX, y: event.clientY }, img, event.currentTarget, layout));
  };

  if (!images.length) return <p role="status">Product image unavailable</p>;
  return (
    <div ref={gallery} className="product-gallery">
      <div className="product-gallery__frame" onPointerMove={hover} onPointerLeave={() => setLens(null)}>
        <button
          ref={opener}
          type="button"
          className="product-gallery__zoom"
          aria-label="Enlarge product image"
          disabled={failed}
          onClick={() => {
            setLens(null);
            resetZoom();
            dialog.current?.showModal();
            setViewerOpen(true);
          }}
        >
          <img
            ref={image}
            className="product-gallery__image"
            src={url}
            alt={alt}
            onLoad={() => setLoadedUrl(url)}
            onError={() => {
              setFailedUrl(url);
              setLens(null);
            }}
          />
        </button>
        {failed && <p role="status">Product image unavailable</p>}
        {arrows()}
        {lens && (
          <>
            <span
              className="product-gallery__lens"
              style={{
                left: lens.left + lens.x,
                top: lens.top + lens.y,
                width: lens.previewWidth / ZOOM_SCALE,
                height: lens.previewHeight / ZOOM_SCALE,
              }}
            />
            <div
              className="product-gallery__magnified"
              aria-hidden="true"
              style={{
                left: `calc(100% + ${lens.gap}px)`,
                width: lens.previewWidth,
                height: lens.previewHeight,
                backgroundImage: `url(${JSON.stringify(url)})`,
                backgroundSize: `${lens.width * ZOOM_SCALE}px ${lens.height * ZOOM_SCALE}px`,
                backgroundPosition: `${-lens.x * ZOOM_SCALE}px ${-lens.y * ZOOM_SCALE}px`,
              }}
            />
          </>
        )}
      </div>
      {images.length > 1 && (
        <div className="product-gallery__thumbnails" aria-label="Product images">
          {images.map((src, index) => (
            <button
              type="button"
              key={`${src}-${index}`}
              aria-label={`View image ${index + 1}`}
              aria-pressed={active === index}
              onClick={() => select(index)}
              className="product-gallery__thumbnail"
            >
              <img src={src} alt="" />
            </button>
          ))}
        </div>
      )}
      {/* Native dialog traps focus and handles Escape; explicitly return focus on every close path. */}
      <dialog
        ref={dialog}
        aria-label="Enlarged product image"
        onClose={() => {
          setViewerOpen(false);
          resetZoom();
          opener.current?.focus();
        }}
        onClick={(event) => {
          if (event.target === event.currentTarget) {
            const rect = event.currentTarget.getBoundingClientRect();
            if (
              event.clientX < rect.left ||
              event.clientX > rect.right ||
              event.clientY < rect.top ||
              event.clientY > rect.bottom
            )
              dialog.current?.close();
          }
        }}
        onKeyDown={(event) => {
          if (images.length > 1 && (event.key === 'ArrowLeft' || event.key === 'ArrowRight')) {
            event.preventDefault();
            move(event.key === 'ArrowLeft' ? -1 : 1);
          }
        }}
      >
        <button
          type="button"
          autoFocus
          className="product-gallery__close"
          aria-label="Close image"
          onClick={() => dialog.current?.close()}
        >
          ×
        </button>
        <div
          ref={surface}
          className="product-gallery__zoom-surface"
          {...handlers}
          onDoubleClick={() => {
            if (lastPointerType.current === 'mouse') toggleZoom();
          }}
        >
          {failed ? (
            <p role="status">Product image unavailable</p>
          ) : (
            <img
              ref={enlarged}
              className="product-gallery__enlarged"
              src={url}
              alt={alt}
              draggable={false}
              style={{
                transform: `translate(${zoom.x}px, ${zoom.y}px) scale(${zoom.scale})`,
              }}
              onError={() => {
                setFailedUrl(url);
                setLens(null);
                resetZoom();
              }}
            />
          )}
        </div>
        {arrows()}
      </dialog>
    </div>
  );
}
